import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchQuickBitesMenu, BubbleTeaMenuItemResponse, InstantCakeItemResponse } from '../api/bubbletea';
import axiosInstance from '../api/axios';
import { useQBCart } from '../context/CartContext';
import { computeOptionsExtraPrice, splitMultiValueByKnownNames, sanitizeOptionSelections } from '../utils/bubbleTeaOptionPricing';
import { getLocalizedField } from '../utils/localize';
import type { SupportedLanguage } from '../i18n';
import type { BubbleTeaOptionCategory, BubbleTeaOptionItem } from '../api/adminBubbleTeaOptions';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
const resolveImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('http')) return url;
  return `${API_ORIGIN}${url}`;
};

// Computes the default option selections (one default item per non-topping
// category) for a freshly-opened bubble tea options modal. Pulled out of
// handleAddBubbleTea so the same logic can be reused by the backfill effect
// that recovers defaults after a failed-then-retried options fetch.
export const computeDefaultOptionSelections = (categories: BubbleTeaOptionCategory[]): Record<string, string> => {
  const defaults: Record<string, string> = {};
  categories.forEach((cat: BubbleTeaOptionCategory) => {
    if (cat.selection_mode !== 'multiple') {
      const defaultItem = cat.items?.find((i: BubbleTeaOptionItem) => i.active && i.is_default)
        ?? cat.items?.find((i: BubbleTeaOptionItem) => i.active);
      if (defaultItem) defaults[cat.name] = defaultItem.name;
    }
  });
  return defaults;
};

// ─── Teal/Green colour tokens ────────────────────────────────────────────────

const QB_PRIMARY = '#1A9E8F';     // teal (variation B)
const QB_PRIMARY_DARK = '#147D72'; // darker teal (variation B)
const QB_LIGHT_BG = '#F0FAFA';    // very light teal/mint (variation B)
const QB_SECTION_BG = '#E6F4F2';  // slightly darker light teal (variation B)

// ─── Styled components ───────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: calc(var(--header-height, 70px) + 1rem);
  padding-bottom: 3rem;
  background-color: ${QB_LIGHT_BG};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageTitle = styled.h1`
  color: ${QB_PRIMARY};
  margin-bottom: 0.5rem;
  font-size: 2rem;
`;

const PageSubtitle = styled.p`
  color: var(--text-color);
  margin-bottom: 2rem;
  opacity: 0.7;
`;

const DeepLinkNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #FFF4E5;
  border: 1px solid #F0B429;
  color: #8A5A00;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const DeepLinkNoticeDismiss = styled.button`
  background: transparent;
  border: none;
  color: #8A5A00;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.25rem;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
`;

// Menu section
const MenuSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  color: ${QB_PRIMARY};
  font-size: 1.4rem;
  margin: 0;
`;

const SectionBadge = styled.span`
  background: ${QB_SECTION_BG};
  color: ${QB_PRIMARY_DARK};
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.7rem;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (min-width: 540px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const MenuCard = styled.div<{ $unavailable?: boolean; $highlighted?: boolean }>`
  background: var(--white, #fff);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${props => props.$highlighted ? '0 0 0 3px ' + QB_PRIMARY + ', 0 6px 20px rgba(26, 158, 143, 0.35)' : '0 2px 8px rgba(0, 0, 0, 0.07)'};
  display: flex;
  flex-direction: column;
  opacity: ${props => props.$unavailable ? 0.55 : 1};
  transition: transform 0.15s, box-shadow 0.3s;
  border: 1px solid ${props => props.$highlighted ? QB_PRIMARY : 'rgba(26, 158, 143, 0.1)'};

  &:hover {
    transform: ${props => props.$unavailable ? 'none' : 'translateY(-3px)'};
    box-shadow: ${props => props.$unavailable ? '0 2px 8px rgba(0,0,0,0.07)' : '0 6px 20px rgba(26, 158, 143, 0.15)'};
  }
`;

const MenuCardImage = styled.div<{ $src?: string | null }>`
  width: 100%;
  height: 170px;
  background-color: ${QB_SECTION_BG};
  background-image: ${props => props.$src ? `url(${props.$src})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
`;

const MenuCardBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MenuCardName = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1rem;
  color: var(--text-color);
`;

const MenuCardDescription = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: var(--text-color);
  opacity: 0.65;
  flex: 1;
  line-height: 1.4;
`;

const MenuCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Price = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
  color: ${QB_PRIMARY};
`;

const UnavailableBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.6rem;
  background: var(--gray-medium, #ccc);
  color: var(--white, #fff);
  border-radius: 20px;
`;

const AddToCartButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${QB_PRIMARY};
  color: var(--white, #fff);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${QB_PRIMARY_DARK};
  }

  &:disabled {
    background-color: var(--gray-medium, #ccc);
    cursor: not-allowed;
  }
`;

const QBCategoryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 2rem;
  position: sticky;
  top: var(--header-height, 70px);
  z-index: 10;
  background-color: ${QB_LIGHT_BG};
  padding: 0.75rem 0;

  @media (max-width: 767px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const QBCategoryButton = styled.button`
  padding: 0.45rem 1.1rem;
  min-height: 44px;
  border-radius: 20px;
  border: 2px solid ${QB_PRIMARY};
  background-color: transparent;
  color: ${QB_PRIMARY};
  font-size: clamp(0.75rem, 2vw, 0.9rem);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.15s, color 0.15s;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;

  @media (max-width: 767px) {
    max-width: 45vw;
  }

  &:hover {
    background-color: ${QB_PRIMARY};
    color: #fff;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 480px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1001;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  color: ${QB_PRIMARY};
`;

const OptionSection = styled.div`
  margin-bottom: 1rem;
`;

const OptionLabel = styled.p`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.4rem 0.75rem;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.88rem;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;

  input:checked ~ & { background: ${QB_LIGHT_BG}; }

  &:hover {
    border-color: ${QB_PRIMARY};
    background: ${QB_LIGHT_BG};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface QBCategoryOrder {
  name: string;
  display_order: number;
}

const QuickBitesMenu: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx / ProductDetail.tsx pattern: i18next's
  // language can be a region-qualified tag or unset, so narrow to the two
  // v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const { addQBItem } = useQBCart();

  const [selectedBubbleTea, setSelectedBubbleTea] = useState<BubbleTeaMenuItemResponse | null>(null);
  const [optionSelections, setOptionSelections] = useState<Record<string, string>>({});
  const [highlightedInstantCakeId, setHighlightedInstantCakeId] = useState<number | null>(null);
  const [deepLinkNotFound, setDeepLinkNotFound] = useState(false);
  const [categoryDeepLinkNotFound, setCategoryDeepLinkNotFound] = useState(false);

  // Scopes the options fetch to the specific product being added, so the
  // modal only shows/allows options actually available for that product
  // (task-028-fix-7 / story-006-2) -- mirrors QBCartDrawer.tsx's edit-modal
  // scoping (task-028-s5-3), but as a single in-place rescope rather than a
  // second query, since this file has no other consumer needing the unscoped
  // view (every consumer here is already gated on the modal being open).
  const activeMenuItemId: number | null = selectedBubbleTea?.menu_item_id ?? null;

  const { data: rawOptionCategories, isLoading: optionsLoading, isError: optionsError, refetch: refetchOptionCategories } = useQuery<BubbleTeaOptionCategory[]>({
    queryKey: ['bubbleTeaOptionCategories', activeMenuItemId],
    queryFn: async () => {
      const res = await axiosInstance.get<BubbleTeaOptionCategory[]>('/quick-bites/options/categories', { params: { menu_item_id: activeMenuItemId } });
      return res.data;
    },
    enabled: activeMenuItemId != null,
  });
  const optionCategories: BubbleTeaOptionCategory[] = Array.isArray(rawOptionCategories)
    ? rawOptionCategories.filter((c: BubbleTeaOptionCategory) => c.active)
    : [];

  const optionCategoriesKey = useMemo(() => {
    if (!Array.isArray(rawOptionCategories)) return '';
    return rawOptionCategories
      .map((c: BubbleTeaOptionCategory) => `${c.id}:${c.active ? 1 : 0}:${(c.items || [])
        .map((i: BubbleTeaOptionItem) => `${i.id}:${i.active ? 1 : 0}:${i.is_default ? 1 : 0}`)
        .join(',')}`)
      .join('|');
  }, [rawOptionCategories]);

  // Fetch menu via react-query
  // task-059 QA cycle 2 fix 1 of 3: `isPending` (react-query v5) is a strict
  // superset of `isLoading` -- true whenever `status === 'pending'`
  // regardless of `fetchStatus` ('fetching' OR 'paused'). `isLoading` itself
  // is kept unchanged below for the unrelated top-level full-page loading
  // gate further down this file.
  const { data: menu, isLoading, isPending: isMenuPending, error: menuError } = useQuery({
    queryKey: ['quickBitesMenu'],
    queryFn: fetchQuickBitesMenu,
  });

  // task-059 QA cycle 2 fix 1 of 3: see isMenuPending's comment above -- same
  // rationale applies to this query's own isCategoriesPending.
  const { data: qbCategoryData, isPending: isCategoriesPending, isError: isCategoriesError } = useQuery({
    queryKey: ['qbPublicCategories'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ categories: QBCategoryOrder[] }>('/quick-bites/categories');
      return res.data;
    },
  });

  const [searchParams] = useSearchParams();
  const { key: locationKey } = useLocation();

  // ─── Scroll to category section when ?category=<slug> is present ──────────

  // task-059 subtask 1 (instrumented investigation, since reverted): this
  // effect never fires `scrollIntoView` more than once per deep-link visit
  // (StrictMode's dev-only double-invoke only duplicates the earlier no-op
  // run before `menu` has loaded, confirmed via console-log-instrumented
  // dev/prod builds — the "React re-render loop" hypothesis is ruled out).
  // The real, confirmed-via-Resource-Timing-API mechanism: this effect reads
  // the target element's layout position at a moment when the SEPARATE
  // `qbPublicCategories` query (feeding `qbOrderMap`, used only by
  // `btGrouped`/`icGrouped` below, not in this effect's own deps) may not
  // have resolved yet, so `btGrouped` is still using the `?? 9999` alphabetic
  // fallback order. On a "warm" browser context (any prior request to this
  // origin already completed) the `/quick-bites/menu` response reliably wins
  // that race a few ms earlier than `/quick-bites/categories`, so the single
  // `scrollIntoView` call locks onto the section's alphabetically-sorted (and
  // possibly-thousands-of-px-away) position; a split second later
  // `qbOrderMap` resolves, `btGrouped` re-sorts, and the section reflows to
  // its correct near-top position while the already-in-flight smooth-scroll
  // animation keeps heading for the stale target — landing exactly the
  // "scrolled to the right element by ID but thousands of px past its real
  // position" overshoot this bug describes. See known-issues.md's
  // `[queued: task-059]` entry for the full empirical writeup.
  //
  // task-059 subtask 2: mirrors the sibling `?item=`/`?type=` effect's
  // `deepLinkKeyRef` idempotency guard below. This does NOT by itself fix the
  // overshoot above (subtask 1 confirmed a single guaranteed call still
  // overshoots on a warm context) -- it closes a separate, real-in-principle
  // risk: without a guard, EVERY `menu` reference change (initial
  // resolution, a background react-query refetch, a StrictMode double-invoke
  // once `menu` is already loaded) re-runs `el.scrollIntoView`, not just the
  // first. The overshoot fix itself lands in subtask 3.
  // task-059 subtask 3: empirically confirmed (Playwright script against the
  // real dev server + fixture-heavy dev DB, 8 warm-context runs) that an
  // instant-scroll swap ALONE does not fix the overshoot -- 6/8 runs still
  // landed ~22600px off target, byte-identical to the pre-fix `smooth`
  // behavior. Root cause: `el.scrollIntoView()` (whether 'smooth' or 'auto')
  // computes its target offset from the element's CURRENT layout position at
  // CALL time; it does not track/retarget the element if a later reflow moves
  // it. So if `btGrouped`/`icGrouped` are still using the `?? 9999` fallback
  // order (because `qbPublicCategories` hasn't resolved yet) at the exact
  // moment this effect runs, an instant scroll jumps immediately to that
  // wrong position -- it just gets there faster than the old smooth version
  // did, it does not get there correctly. Fix extended per this subtask's own
  // authorization: gate the effect on `isCategoriesLoading` (the
  // `qbPublicCategories` query settling, success or error) in addition to the
  // instant-scroll swap. While still loading, the effect returns without
  // consuming `categoryDeepLinkKeyRef`, so it fires again once
  // `isCategoriesLoading` flips false (by which point `qbOrderMap`/
  // `btGrouped`/`icGrouped` have already resolved to their final order for
  // this render, so the DOM position read here is stable). Re-verified via
  // the same Playwright script: 8/8 warm-context runs land within a few px of
  // the target's top after this change.
  // task-059 fix-4 (QA Edge Case Auditor MEDIUM finding): the ref above was
  // keyed on the raw `category` string alone and never reset, so a genuine
  // repeat visit to the IDENTICAL ?category= value while the component stays
  // mounted (e.g. a client-side <Link> click to the same deep link) was
  // silently a no-op for the rest of that mount's lifetime, even after the
  // customer scrolled away. Fixed by keying on a composite
  // `${category}::${location.key}` instead: react-router's `useLocation().key`
  // is a fresh unique string on every genuine push/replace navigation but
  // stays unchanged across a plain re-render or background react-query
  // refetch, so a true re-navigation to the same URL is treated as a fresh
  // visit (still fully subject to the isCategoriesLoading/isCategoriesError
  // gates above), while a background refetch or StrictMode double-invoke
  // still correctly no-ops exactly as before.
  const categoryDeepLinkKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const category = searchParams.get('category');
    if (!category) return;
    const dedupeKey = `${category}::${locationKey}`;
    if (categoryDeepLinkKeyRef.current === dedupeKey) return;
    // task-059 QA cycle 2 fix 1 of 3: was `isCategoriesLoading`, which reads
    // `false` while a query is genuinely offline/paused (fetchStatus:
    // 'paused', status: 'pending') under this app's default `networkMode:
    // 'online'` -- letting this gate fall through against still-unresolved
    // data. `isCategoriesPending` (true for fetching AND paused AND
    // not-yet-started) correctly keeps waiting through that gap.
    if (isCategoriesPending) return; // wait for qbOrderMap to settle before reading layout
    // task-059 QA cycle 2 fix 2 of 3 (QA Edge Case Auditor MEDIUM finding):
    // isCategoriesError used to be an unconditional early-return right here,
    // BEFORE the element lookup and the not-found branch below ever ran -- so
    // a bad/renamed slug during a genuine qbPublicCategories outage was
    // silently swallowed (console.warn only, never the user-facing
    // categoryDeepLinkNotFound notice). Category EXISTENCE is fully decidable
    // from menu/isMenuPending/menuError alone: a qbPublicCategories outage
    // only affects sort ORDER via btGrouped/icGrouped's `?? 9999` fallback,
    // never which qb-bt-*/qb-ic-* DOM elements exist. Only the confident
    // SCROLL POSITION genuinely needs qbPublicCategories settled -- so
    // isCategoriesError now only gates the scroll itself below (mirroring the
    // sibling ?item=/?type= effect's `if (!isCategoriesError) { scroll } else
    // { warn } ` shape), not whether the not-found determination ever runs.
    const slug = category.toLowerCase().replace(/\s+/g, '-');
    const el = document.getElementById(`qb-bt-${slug}`) ?? document.getElementById(`qb-ic-${slug}`);
    if (el) {
      setCategoryDeepLinkNotFound(false);
      categoryDeepLinkKeyRef.current = dedupeKey;
      if (!isCategoriesError) {
        // Force a genuinely instant jump despite GlobalStyles.ts's sitewide
        // `html { scroll-behavior: smooth }` -- same pattern as App.tsx's
        // ScrollToTop (see frontend.md's "Scroll Behavior" note): temporarily
        // override with an inline style, force a synchronous layout flush
        // (Chromium resolves scroll-behavior's used value lazily and otherwise
        // silently no-ops the override), scroll, then restore.
        const html = document.documentElement;
        const previousScrollBehavior = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        void html.offsetHeight;
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        html.style.scrollBehavior = previousScrollBehavior;
      } else {
        // qbPublicCategories genuinely failed (not racing) — qbOrderMap stays permanently
        // empty this session, so btGrouped/icGrouped are stuck on the ??9999 alphabetical
        // fallback order. Scrolling now would confidently land on a position that may not
        // match where the customer expects this category — the exact "confidently wrong"
        // failure mode this task exists to prevent, just triggered by a backend error
        // instead of a timing race. The category itself is known to exist (marked handled
        // above) — only the scroll is skipped.
        console.warn(`QuickBitesMenu: skipping ?category=${category} deep-link scroll — qbPublicCategories failed to load, category order may be wrong this session`);
      }
    } else if (!isMenuPending && !menuError) {
      // menu resolved successfully — but no bubble-tea/instant-cake section matches this
      // slug: a mistyped/renamed/removed category, or a real category with zero items so
      // its section never renders. This determination no longer depends on
      // isCategoriesError (see comment above) — category existence is decidable from menu
      // data alone, exactly like the sibling ?item=/?type= effect's own .find() calls.
      // Consume the ref so this doesn't re-warn/re-set the notice on every subsequent
      // render, mirroring that sibling effect's not-found branch.
      // task-059 QA cycle 2 fix 1 of 3: was `!isLoading`, which reads `true`
      // (misread as "settled") while `menu` is genuinely offline/paused --
      // `!isMenuPending` correctly withholds the not-found notice until menu
      // has actually resolved (success or error).
      categoryDeepLinkKeyRef.current = dedupeKey;
      console.warn(`QuickBitesMenu: skipping ?category=${category} deep-link scroll — no matching category section found in menu data`);
      setCategoryDeepLinkNotFound(true);
    }
  }, [searchParams, menu, isCategoriesPending, isCategoriesError, isMenuPending, menuError, locationKey]);

  // ─── Backfill default option selections after a failed-then-retried fetch ──
  // handleAddBubbleTea computes defaults from optionCategories at the moment
  // the modal opens; if the options fetch was still erroring/empty at that
  // instant, no defaults could be computed. Once a Retry succeeds and
  // optionCategories becomes populated while the modal is still open, this
  // backfills the same defaults a normal successful first-load would have
  // shown — but only when nothing has been selected yet, so it never
  // clobbers a real customer choice.
  useEffect(() => {
    if (!selectedBubbleTea) return;               // modal not open
    if (optionCategories.length === 0) return;     // still empty (loading/error) — nothing to backfill yet
    if (Object.keys(optionSelections).length > 0) return; // customer already has a selection — never overwrite
    const defaults = computeDefaultOptionSelections(optionCategories);
    if (Object.keys(defaults).length > 0) setOptionSelections(defaults);
  }, [optionCategoriesKey, selectedBubbleTea]);

  const qbOrderMap = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    (qbCategoryData?.categories ?? []).forEach((c: QBCategoryOrder) => map.set(c.name, c.display_order));
    return map;
  }, [qbCategoryData]);

  const bubbleTeas: BubbleTeaMenuItemResponse[] = Array.isArray(menu?.bubble_tea) ? menu!.bubble_tea : [];
  const instantCakes: InstantCakeItemResponse[] = Array.isArray(menu?.instant_cakes) ? menu!.instant_cakes : [];

  // ─── Category grouping ────────────────────────────────────────────────────

  const btGrouped = useMemo<Map<string, BubbleTeaMenuItemResponse[]>>(() => {
    const map = new Map<string, BubbleTeaMenuItemResponse[]>();
    bubbleTeas.forEach(item => {
      const key = item.menu_category ?? 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return new Map(Array.from(map.entries()).sort(([a], [b]) => {
      const oa = qbOrderMap.get(a) ?? 9999;
      const ob = qbOrderMap.get(b) ?? 9999;
      return oa !== ob ? oa - ob : a.localeCompare(b);
    }));
  }, [bubbleTeas, qbOrderMap]);

  const icGrouped = useMemo<Map<string, InstantCakeItemResponse[]>>(() => {
    const map = new Map<string, InstantCakeItemResponse[]>();
    instantCakes.forEach(item => {
      const key = item.category ?? 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return new Map(Array.from(map.entries()).sort(([a], [b]) => {
      // 'Other' holds uncategorised IC items and must always sort last.
      if (a === 'Other' && b !== 'Other') return 1;
      if (b === 'Other' && a !== 'Other') return -1;
      const oa = qbOrderMap.get(a) ?? 9999;
      const ob = qbOrderMap.get(b) ?? 9999;
      return oa !== ob ? oa - ob : a.localeCompare(b);
    }));
  }, [instantCakes, qbOrderMap]);

  const toSlug = (category: string) => category.toLowerCase().replace(/\s+/g, '-');

  // ─── Cart helpers ─────────────────────────────────────────────────────────

  const handleAddBubbleTea = (item: BubbleTeaMenuItemResponse) => {
    // optionCategories is scoped by activeMenuItemId (derived from
    // selectedBubbleTea), which is still the previous item's id at the moment
    // this handler runs -- so it can't yet reflect the newly-selected item's
    // categories. Defaults are populated by the pre-existing backfill effect
    // below once the newly-scoped fetch resolves after setSelectedBubbleTea.
    setOptionSelections({});
    setSelectedBubbleTea(item);
  };

  // ─── Deep-link handling for ?item=<id>&type=bubble_tea|instant_cake ────────
  // Extends the ?category= scroll-effect above (for QR-code / featured-item
  // links that target one specific menu item rather than a whole category
  // section). For bubble tea, scrolls to the matching card AND opens its
  // ordering options modal via the exact same handler the card's own
  // "Add to Cart" button uses, so add-to-cart from here behaves identically
  // to reaching the item via the normal menu flow. For instant cakes, only
  // scrolls to and briefly highlights the matching card — no modal exists
  // for instant cakes (they're add-to-cart-inline, per J16).
  // `deepLinkKeyRef` ensures this only fires once per unique item/type pair
  // in the URL — once handled, a later background refetch of the menu (new
  // array reference, same content) won't re-trigger the scroll/modal-open
  // and clobber whatever the customer is doing.
  const deepLinkKeyRef = useRef<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // task-059 subtask 4: identical instant-scroll pattern to the ?category=
  // effect above (see its inline comment for the Chromium
  // forced-reflow/GlobalStyles.ts rationale) — factored into a tiny local
  // helper here since this effect needs it at two call sites (bubble_tea and
  // instant_cake branches). The `?category=` effect itself is deliberately
  // left untouched/un-refactored.
  const scrollIntoViewInstant = (el: HTMLElement | null, block: ScrollLogicalPosition) => {
    if (!el) return;
    const html = document.documentElement;
    const previousScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    void html.offsetHeight;
    el.scrollIntoView({ behavior: 'auto', block });
    html.style.scrollBehavior = previousScrollBehavior;
  };

  // task-059 subtask 1 (instrumented investigation, since reverted):
  // confirmed this sibling effect shares the exact same overshoot mechanism
  // as the `?category=` effect above, even though it already has the
  // `deepLinkKeyRef` idempotency guard -- the guard only stops a LATER
  // re-run of this effect body from re-handling the same key, it does
  // nothing to protect the ONE scrollIntoView call it does make from the
  // qbOrderMap-reorder race described above, since that race can still
  // relocate the target card after this effect's single call has already
  // fired. A guarded single call is therefore not sufficient here either.
  // task-059 subtask 4: this sibling shares the identical mechanism as the
  // `?category=` effect (confirmed by subtask 1's instrumentation) — the
  // target card's DOM position also depends on `btGrouped`/`icGrouped`,
  // which resort by `qbOrderMap` (fed by the separate `qbPublicCategories`
  // query) once it resolves. Applying the SAME two-part fix proven on that
  // effect: (1) swap `behavior: 'smooth'` for the instant/forced-reflow
  // pattern above, and (2) gate the whole effect on `isCategoriesLoading`
  // settling, added to the dependency array, so it re-fires once
  // `qbOrderMap` has resolved to its final order rather than reading a
  // stale/fallback-ordered layout position. The early return below sits
  // before `deepLinkKeyRef` is ever consumed, so nothing (including the
  // "not found" warning branch and the bubble-tea modal auto-open) fires
  // early against a not-yet-resettled layout — everything in this effect,
  // not just the scroll, now waits for both `menu` and `qbPublicCategories`
  // to have resolved. In practice this only delays modal-open by however
  // long `qbPublicCategories` (a small, fast lookup) takes past `menu`
  // resolving, and matches the `?category=` effect's own established
  // gating shape exactly.
  // task-059 fix-4: this sibling's `deepLinkKeyRef` has the same never-resets
  // property as the `?category=` guard above -- pre-existing on `main` before
  // task-059 (not itself a task-introduced regression, per known-issues.md),
  // but folded into the same fix for consistency. `key` below now includes
  // `locationKey` for the identical reason: a genuine re-navigation to the
  // same ?item=&type= URL re-fires, a background refetch/re-render doesn't.
  useEffect(() => {
    const itemParam = searchParams.get('item');
    const typeParam = searchParams.get('type');
    if (!itemParam || !typeParam) return;
    const key = `${itemParam}:${typeParam}:${locationKey}`;
    if (deepLinkKeyRef.current === key) return;
    // task-059 QA cycle 2 fix 1 of 3: was `isCategoriesLoading` -- see the
    // `?category=` effect's identical comment above for the offline/paused
    // `isCategoriesPending` rationale, which applies unchanged here.
    if (isCategoriesPending) return; // wait for qbOrderMap to settle before reading layout

    const itemId = Number(itemParam);
    if (Number.isNaN(itemId)) return;

    if (typeParam === 'bubble_tea') {
      const match = bubbleTeas.find(bt => bt.menu_item_id === itemId);
      if (!match) {
        // menu may not have loaded yet — retry on next data update. Only once
        // loading has genuinely settled with still no match (and no fetch
        // error) do we warn, surface the "no longer available" notice, and
        // stop retrying (otherwise every background refetch re-runs this).
        // task-059 QA cycle 2 fix 1 of 3: was `!isLoading`, which misreads an
        // offline/paused `menu` query as "settled" -- `!isMenuPending`
        // correctly withholds the not-found notice until menu has actually
        // resolved.
        if (!isMenuPending && !menuError) {
          console.warn(`QuickBitesMenu: deep-link item id=${itemId} type=bubble_tea not found in menu data`);
          deepLinkKeyRef.current = key;
          setDeepLinkNotFound(true);
        }
        return;
      }
      deepLinkKeyRef.current = key;
      setDeepLinkNotFound(false);
      if (!isCategoriesError) {
        scrollIntoViewInstant(document.getElementById(`qb-card-bt-${itemId}`), 'center');
      } else {
        console.warn(`QuickBitesMenu: skipping ?item=${itemId}&type=bubble_tea deep-link scroll — qbPublicCategories failed to load, category order may be wrong this session`);
      }
      if (!match.available) return;
      handleAddBubbleTea(match);
    } else if (typeParam === 'instant_cake') {
      const match = instantCakes.find(ic => ic.id === itemId);
      if (!match) {
        // task-059 QA cycle 2 fix 1 of 3: see the bubble_tea branch's
        // identical comment above -- `!isMenuPending` replaces `!isLoading`
        // for the same offline/paused-query reason.
        if (!isMenuPending && !menuError) {
          console.warn(`QuickBitesMenu: deep-link item id=${itemId} type=instant_cake not found in menu data`);
          deepLinkKeyRef.current = key;
          setDeepLinkNotFound(true);
        }
        return;
      }
      deepLinkKeyRef.current = key;
      setDeepLinkNotFound(false);
      if (!isCategoriesError) {
        scrollIntoViewInstant(document.getElementById(`qb-card-ic-${itemId}`), 'center');
      } else {
        console.warn(`QuickBitesMenu: skipping ?item=${itemId}&type=instant_cake deep-link scroll — qbPublicCategories failed to load, category order may be wrong this session`);
      }
      setHighlightedInstantCakeId(itemId);
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedInstantCakeId(null);
        highlightTimeoutRef.current = null;
      }, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, bubbleTeas, instantCakes, isMenuPending, menuError, isCategoriesPending, isCategoriesError, locationKey]);

  // Cleanup-only effect for the highlight timer above. Deliberately kept
  // separate from the deep-link effect itself (rather than returning a
  // cleanup from that effect) — a cleanup there would fire on every re-run
  // of that effect (e.g. a background menu refetch mid-highlight), cancelling
  // the timer prematurely. This effect's empty deps mean its cleanup only
  // runs on unmount, which is exactly when we need to avoid a post-unmount
  // setState.
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const confirmAddBubbleTea = () => {
    if (!selectedBubbleTea) return;
    const sanitizedSelections = sanitizeOptionSelections(optionSelections, optionCategories);
    const finalPrice = selectedBubbleTea.price + computeOptionsExtraPrice(sanitizedSelections, optionCategories);
    const newOptions = Object.keys(sanitizedSelections).length > 0 ? { ...sanitizedSelections } : undefined;
    addQBItem({
      type: 'bubble_tea',
      itemId: selectedBubbleTea.menu_item_id,
      name: selectedBubbleTea.name,
      price: finalPrice,
      quantity: 1,
      options: newOptions,
    });
    setSelectedBubbleTea(null);
    setOptionSelections({});
  };

  const handleAddInstantCake = (item: InstantCakeItemResponse) => {
    addQBItem({
      type: 'instant_cake',
      itemId: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
    });
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const formatPrice = (p: number) => `£${p.toFixed(2)}`;

  // ─── Render states ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <PageTitle>{t('quickBitesMenu.pageTitle')}</PageTitle>
          <p>{t('quickBitesMenu.loadingMenu')}</p>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (menuError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <PageTitle>{t('quickBitesMenu.pageTitle')}</PageTitle>
          <p style={{ color: '#e53e3e' }}>{t('quickBitesMenu.loadError')}</p>
        </ContentWrapper>
      </PageContainer>
    );
  }

  // ─── Main menu view ───────────────────────────────────────────────────────

  return (
    <PageContainer>
      <ContentWrapper>
        <PageTitle>{t('quickBitesMenu.pageTitle')}</PageTitle>
        <PageSubtitle>{t('quickBitesMenu.subtitle')}</PageSubtitle>

        {categoryDeepLinkNotFound && (
          <DeepLinkNotice role="status">
            <span>{t('quickBitesMenu.deepLinkCategoryNotFound')}</span>
            <DeepLinkNoticeDismiss onClick={() => setCategoryDeepLinkNotFound(false)} aria-label={t('quickBitesMenu.dismissAriaLabel')}>×</DeepLinkNoticeDismiss>
          </DeepLinkNotice>
        )}

        {deepLinkNotFound && (
          <DeepLinkNotice role="status">
            <span>{t('quickBitesMenu.deepLinkNotFound')}</span>
            <DeepLinkNoticeDismiss onClick={() => setDeepLinkNotFound(false)} aria-label={t('quickBitesMenu.dismissAriaLabel')}>×</DeepLinkNoticeDismiss>
          </DeepLinkNotice>
        )}

        <QBCategoryBar>
          {Array.from(btGrouped.keys()).map(cat => (
            <QBCategoryButton
              key={`bt-${cat}`}
              onClick={() =>
                document.getElementById(`qb-bt-${toSlug(cat)}`)?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              🧋 {cat}
            </QBCategoryButton>
          ))}
          {Array.from(icGrouped.keys()).map(cat => (
            <QBCategoryButton
              key={`ic-${cat}`}
              onClick={() =>
                document.getElementById(`qb-ic-${toSlug(cat)}`)?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              🍰 {cat}
            </QBCategoryButton>
          ))}
        </QBCategoryBar>

        <Layout>
          <MenuSection>
            {/* Bubble Tea Section */}
            <div>
              <SectionHeader>
                <SectionTitle>{t('quickBitesMenu.bubbleTeaSectionTitle')}</SectionTitle>
                <SectionBadge>{t(bubbleTeas.length === 1 ? 'quickBitesMenu.itemCountSingular' : 'quickBitesMenu.itemCountPlural', { count: bubbleTeas.length })}</SectionBadge>
              </SectionHeader>
              {bubbleTeas.length === 0 ? (
                <p style={{ color: 'var(--text-color)', opacity: 0.5 }}>{t('quickBitesMenu.noBubbleTeaAvailable')}</p>
              ) : (
                Array.from(btGrouped.entries()).map(([cat, items]) => (
                  <div key={`bt-cat-${cat}`} id={`qb-bt-${toSlug(cat)}`} style={{ marginBottom: '2rem' }}>
                    <h3 style={{ color: QB_PRIMARY_DARK, fontSize: '1.1rem', marginBottom: '1rem' }}>{cat}</h3>
                    <MenuGrid>
                      {items.map(item => (
                        <MenuCard key={item.menu_item_id} id={`qb-card-bt-${item.menu_item_id}`} $unavailable={!item.available}>
                          <MenuCardImage $src={resolveImageUrl(item.image_url)}>
                            {!item.image_url && '🧋'}
                          </MenuCardImage>
                          <MenuCardBody>
                            <MenuCardName>{getLocalizedField(item, 'name', language) as string}</MenuCardName>
                            {(getLocalizedField(item, 'description', language) as string | null) && (
                              <MenuCardDescription>{getLocalizedField(item, 'description', language) as string}</MenuCardDescription>
                            )}
                            <MenuCardFooter>
                              <Price>{formatPrice(item.price)}</Price>
                              {item.available ? (
                                <AddToCartButton onClick={() => handleAddBubbleTea(item)}>
                                  {t('quickBitesMenu.addToCart')}
                                </AddToCartButton>
                              ) : (
                                <UnavailableBadge>{t('quickBitesMenu.unavailableBadge')}</UnavailableBadge>
                              )}
                            </MenuCardFooter>
                          </MenuCardBody>
                        </MenuCard>
                      ))}
                    </MenuGrid>
                  </div>
                ))
              )}
            </div>

            {/* Instant Cakes Section */}
            <div>
              <SectionHeader>
                <SectionTitle>{t('quickBitesMenu.instantCakesSectionTitle')}</SectionTitle>
                <SectionBadge>{t(instantCakes.length === 1 ? 'quickBitesMenu.itemCountSingular' : 'quickBitesMenu.itemCountPlural', { count: instantCakes.length })}</SectionBadge>
              </SectionHeader>
              {instantCakes.length === 0 ? (
                <p style={{ color: 'var(--text-color)', opacity: 0.5 }}>{t('quickBitesMenu.noInstantCakesAvailable')}</p>
              ) : (
                Array.from(icGrouped.entries()).map(([cat, items]) => (
                  <div key={`ic-cat-${cat}`} id={`qb-ic-${toSlug(cat)}`} style={{ marginBottom: '2rem' }}>
                    <h3 style={{ color: QB_PRIMARY_DARK, fontSize: '1.1rem', marginBottom: '1rem' }}>{cat}</h3>
                    <MenuGrid>
                      {items.map(item => (
                        <MenuCard
                          key={item.id}
                          id={`qb-card-ic-${item.id}`}
                          $unavailable={!item.active || item.stock_count === 0}
                          $highlighted={highlightedInstantCakeId === item.id}
                        >
                          <MenuCardImage $src={resolveImageUrl(item.image_url)}>
                            {!item.image_url && '🍰'}
                          </MenuCardImage>
                          <MenuCardBody>
                            <MenuCardName>{getLocalizedField(item, 'name', language) as string}</MenuCardName>
                            {(getLocalizedField(item, 'description', language) as string | null) && (
                              <MenuCardDescription>{getLocalizedField(item, 'description', language) as string}</MenuCardDescription>
                            )}
                            <MenuCardFooter>
                              <Price>{formatPrice(item.price)}</Price>
                              {item.active && item.stock_count > 0 ? (
                                <AddToCartButton onClick={() => handleAddInstantCake(item)}>
                                  {t('quickBitesMenu.addToCart')}
                                </AddToCartButton>
                              ) : (
                                <UnavailableBadge>
                                  {item.stock_count === 0 ? t('quickBitesMenu.outOfStockBadge') : t('quickBitesMenu.unavailableBadge')}
                                </UnavailableBadge>
                              )}
                            </MenuCardFooter>
                          </MenuCardBody>
                        </MenuCard>
                      ))}
                    </MenuGrid>
                  </div>
                ))
              )}
            </div>
          </MenuSection>
        </Layout>
      </ContentWrapper>
      {selectedBubbleTea && (() => {
        const liveTotal = selectedBubbleTea.price + computeOptionsExtraPrice(optionSelections, optionCategories);
        return (
        <ModalOverlay onClick={() => { setSelectedBubbleTea(null); setOptionSelections({}); }}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{getLocalizedField(selectedBubbleTea, 'name', language) as string} — {formatPrice(liveTotal)}</ModalTitle>
            {optionsLoading && <p>{t('quickBitesMenu.modalLoadingOptions')}</p>}
            {optionsError && !optionsLoading && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#e53e3e', margin: 0 }}>
                  {t('quickBitesMenu.modalOptionsLoadError')}
                </p>
                <button
                  onClick={() => refetchOptionCategories()}
                  style={{ marginTop: '0.4rem', padding: '0.3rem 0.8rem', background: 'transparent', color: '#e53e3e', border: '2px solid #e53e3e', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  {t('quickBitesMenu.retry')}
                </button>
              </div>
            )}
            {!optionsLoading && optionCategories.map((cat: BubbleTeaOptionCategory) => {
              const activeItems: BubbleTeaOptionItem[] = (cat.items || []).filter((i: BubbleTeaOptionItem) => i.active).sort((a: BubbleTeaOptionItem, b: BubbleTeaOptionItem) => a.sort_order - b.sort_order);
              return (
                <OptionSection key={cat.id}>
                  <OptionLabel>{getLocalizedField(cat, 'name', language) as string}</OptionLabel>
                  <OptionsGrid>
                    {cat.selection_mode !== 'multiple' ? (
                      activeItems.map((item: BubbleTeaOptionItem) => (
                        <OptionRow key={item.id} style={optionSelections[cat.name] === item.name ? { borderColor: QB_PRIMARY, background: QB_LIGHT_BG } : {}}>
                          <input
                            type="radio"
                            name={cat.name}
                            value={item.name}
                            checked={optionSelections[cat.name] === item.name}
                            onChange={() => setOptionSelections(prev => ({ ...prev, [cat.name]: item.name }))}
                          />
                          {getLocalizedField(item, 'name', language) as string}
                        </OptionRow>
                      ))
                    ) : (
                      activeItems.map((item: BubbleTeaOptionItem) => {
                        const currentToppings = optionSelections[cat.name] ? splitMultiValueByKnownNames(optionSelections[cat.name], cat.items).map((t: string) => t.trim()) : [];
                        const checked = currentToppings.includes(item.name);
                        return (
                          <OptionRow key={item.id} style={checked ? { borderColor: QB_PRIMARY, background: QB_LIGHT_BG } : {}}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const newToppings = checked
                                  ? currentToppings.filter(t => t !== item.name)
                                  : [...currentToppings, item.name];
                                setOptionSelections(prev => ({ ...prev, [cat.name]: newToppings.join(',') }));
                              }}
                            />
                            {getLocalizedField(item, 'name', language) as string}
                          </OptionRow>
                        );
                      })
                    )}
                  </OptionsGrid>
                </OptionSection>
              );
            })}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                onClick={confirmAddBubbleTea}
                disabled={optionsLoading}
                style={{ flex: 1, padding: '0.6rem', background: QB_PRIMARY, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, opacity: optionsLoading ? 0.6 : 1 }}
              >
                {t('quickBitesMenu.addToCart')}
              </button>
              <button
                onClick={() => { setSelectedBubbleTea(null); setOptionSelections({}); }}
                style={{ padding: '0.6rem 1rem', background: 'transparent', color: QB_PRIMARY, border: `2px solid ${QB_PRIMARY}`, borderRadius: '8px', cursor: 'pointer' }}
              >
                {t('quickBitesMenu.cancel')}
              </button>
            </div>
          </ModalBox>
        </ModalOverlay>
        );
      })()}
    </PageContainer>
  );
};

export default QuickBitesMenu;
